import { Box, Button, Grid2, Typography } from "@mui/material";
import axios from "axios";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const Step1 = ({ tender, setTender, user, setStep }) => {
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [btnLoading, setBtnLoading] = useState(false);

  const handleChange = (e) => {
    setTender({ ...tender, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    const data = tender || {};

    const isEmpty = (value) => !value || value.trim?.() === "";

    const now = new Date();

    const purchaseDate = data.tenderPurchaseDate
      ? new Date(data.tenderPurchaseDate)
      : null;
    const publishDate = data.tenderPublishDate
      ? new Date(data.tenderPublishDate)
      : null;
    const bidStart = data.bidSubmissionStartDate
      ? new Date(data.bidSubmissionStartDate)
      : null;
    const bidEnd = data.bidSubmissionEndDate
      ? new Date(data.bidSubmissionEndDate)
      : null;
    const bidOpen = data.bidOpeningDate ? new Date(data.bidOpeningDate) : null;

    if (isEmpty(data.title)) newErrors.title = "Title is required";
    if (isEmpty(data.shortDesc))
      newErrors.shortDesc = "Short Description is required";
    if (isEmpty(data.longDesc)) {
      newErrors.longDesc = "Long Description is required";
    } else if (data.longDesc.length > 1000) {
      newErrors.longDesc = "Long Description should be upto 1000 characters";
    }
    if (!purchaseDate) {
      newErrors.tenderPurchaseDate = "Purchase Date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (purchaseDate < today) {
        newErrors.tenderPurchaseDate = "Purchase Date cannot be in the past";
      }
    }

    if (!publishDate) {
      newErrors.tenderPublishDate = "Publish Date is required";
    } else {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (publishDate < today) {
        newErrors.tenderPublishDate = "Publish Date cannot be in the past";
      }
    }

    if (!bidStart) {
      newErrors.bidSubmissionStartDate = "Start Date & Time is required";
    } else if (bidStart < now) {
      newErrors.bidSubmissionStartDate =
        "Start Date & Time cannot be in the past";
    } else {
      publishDate.setHours(0, 0, 0, 0);
      if (bidStart <= publishDate) {
        newErrors.bidSubmissionStartDate =
          "Start Date & Time must be after Tender Publish Date";
      }
    }
    if (!bidEnd) {
      newErrors.bidSubmissionEndDate = "End Date & Time is required";
    } else if (bidEnd < now) {
      newErrors.bidSubmissionEndDate = "End Date & Time cannot be in the past";
    } else if (bidStart && bidEnd <= bidStart) {
      newErrors.bidSubmissionEndDate =
        "End Date & Time must be after Start Date & Time";
    }

    if (!bidOpen) {
      newErrors.bidOpeningDate = "Bid Opening Date & Time is required";
    } else if (bidOpen < now) {
      newErrors.bidOpeningDate =
        "Bid Opening Date & Time cannot be in the past";
    } else if (bidEnd && bidOpen <= bidEnd) {
      newErrors.bidOpeningDate = "Bid Opening must be after Bid Submission End";
    }

    if (isEmpty(data.bidOfferValidity))
      newErrors.bidOfferValidity = "Offer Validity is required";
    if (isEmpty(data.deptName))
      newErrors.deptName = "Department Name is required";
    if (isEmpty(data.contactEmail)) {
      newErrors.contactEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contactEmail)) {
      newErrors.contactEmail = "Enter a valid email";
    }
    if (isEmpty(data.itemCategory))
      newErrors.itemCategory = "Item Category is required";
    if (isEmpty(data.contractPeriod))
      newErrors.contractPeriod = "Contract Period is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft`,
        {
          username: user.username,
          title: tender.title,
          shortDesc: tender.shortDesc,
          longDesc: tender.longDesc,
          tenderPurchaseDate: tender.tenderPurchaseDate,
          tenderPublishDate: tender.tenderPublishDate,
          bidSubmissionStartDate: tender.bidSubmissionStartDate,
          bidSubmissionEndDate: tender.bidSubmissionEndDate,
          bidOpeningDate: tender.bidOpeningDate,
          bidOfferValidity: tender.bidOfferValidity,
          deptName: tender.deptName,
          orgName: tender.orgName,
          contactEmail: tender.contactEmail,
          itemCategory: tender.itemCategory,
          contractPeriod: tender.contractPeriod,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setTender(res.data);
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft?draftTenderId=${tender.draftTenderId}`,
        {
          title: tender.title,
          shortDesc: tender.shortDesc,
          longDesc: tender.longDesc,
          tenderPurchaseDate: tender.tenderPurchaseDate,
          tenderPublishDate: tender.tenderPublishDate,
          bidSubmissionStartDate: tender.bidSubmissionStartDate,
          bidSubmissionEndDate: tender.bidSubmissionEndDate,
          bidOpeningDate: tender.bidOpeningDate,
          bidOfferValidity: tender.bidOfferValidity,
          deptName: tender.deptName,
          contactEmail: tender.contactEmail,
          itemCategory: tender.itemCategory,
          contractPeriod: tender.contractPeriod,
        },
        { headers: { Authorization: `Bearer ${user.accessToken}` } }
      );
      setTender(res.data);
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };
  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <h2>Create New Tender</h2>
      <div className="progress-container">
        <span className="blue-bar" style={{ width: "16%" }}></span>
      </div>
      <div className="dashboard-container">
        <Typography className="form-title">Tender Details</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="title">Title</label>
            <input
              className={`${errors.title && "input-error"}`}
              type="text"
              name="title"
              id="title"
              onChange={handleChange}
              value={tender && tender.title}
            />
            {errors.title && <p className="error">{errors.title}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="shortDesc">Short Description</label>
            <input
              className={`${errors.shortDesc && "input-error"}`}
              type="text"
              name="shortDesc"
              id="shortDesc"
              onChange={handleChange}
              value={tender && tender.shortDesc}
            />
            {errors.shortDesc && <p className="error">{errors.shortDesc}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <label htmlFor="longDesc">Long Description</label>
            <textarea
              className={`${errors.longDesc && "input-error"}`}
              name="longDesc"
              id="longDesc"
              rows={8}
              onChange={handleChange}
              value={(tender && tender.longDesc) || ""}
            ></textarea>
            {errors.longDesc && <p className="error">{errors.longDesc}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="tenderPurchaseDate">Tender Purchase Date</label>
            <input
              className={`${errors.tenderPurchaseDate && "input-error"}`}
              type="date"
              name="tenderPurchaseDate"
              id="tenderPurchaseDate"
              onChange={handleChange}
              value={tender && tender.tenderPurchaseDate}
            />
            {errors.tenderPurchaseDate && (
              <p className="error">{errors.tenderPurchaseDate}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="tenderPublishDate">Tender Publish Date</label>
            <input
              className={`${errors.tenderPublishDate && "input-error"}`}
              type="date"
              name="tenderPublishDate"
              id="tenderPublishDate"
              onChange={handleChange}
              value={tender && tender.tenderPublishDate}
            />
            {errors.tenderPublishDate && (
              <p className="error">{errors.tenderPublishDate}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bidSubmissionStartDate">
              Bid Submission Start Date
            </label>
            <input
              className={`${errors.bidSubmissionStartDate && "input-error"}`}
              type="datetime-local"
              name="bidSubmissionStartDate"
              id="bidSubmissionStartDate"
              onChange={handleChange}
              value={tender && tender.bidSubmissionStartDate}
            />
            {errors.bidSubmissionStartDate && (
              <p className="error">{errors.bidSubmissionStartDate}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bidSubmissionEndDate">
              Bid Submission End Date
            </label>
            <input
              className={`${errors.bidSubmissionEndDate && "input-error"}`}
              type="datetime-local"
              name="bidSubmissionEndDate"
              id="bidSubmissionEndDate"
              onChange={handleChange}
              value={tender && tender.bidSubmissionEndDate}
            />
            {errors.bidSubmissionEndDate && (
              <p className="error">{errors.bidSubmissionEndDate}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bidOpeningDate">Bid Opening Date</label>
            <input
              className={`${errors.bidOpeningDate && "input-error"}`}
              type="datetime-local"
              name="bidOpeningDate"
              id="bidOpeningDate"
              onChange={handleChange}
              value={tender && tender.bidOpeningDate}
            />
            {errors.bidOpeningDate && (
              <p className="error">{errors.bidOpeningDate}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="bidOfferValidity">
              Bid Offer Validity (In Days)
            </label>
            <input
              className={`${errors.bidOfferValidity && "input-error"}`}
              type="number"
              name="bidOfferValidity"
              id="bidOfferValidity"
              onChange={handleChange}
              value={tender && tender.bidOfferValidity}
            />
            {errors.bidOfferValidity && (
              <p className="error">{errors.bidOfferValidity}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="deptName">Department Name</label>
            <input
              className={`${errors.deptName && "input-error"}`}
              type="text"
              name="deptName"
              id="deptName"
              onChange={handleChange}
              value={tender && tender.deptName}
            />
            {errors.deptName && <p className="error">{errors.deptName}</p>}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contactEmail">Contact Email</label>
            <input
              className={`${errors.contactEmail && "input-error"}`}
              type="email"
              name="contactEmail"
              id="contactEmail"
              onChange={handleChange}
              value={tender && tender.contactEmail}
            />
            {errors.contactEmail && (
              <p className="error">{errors.contactEmail}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="itemCategory">Item Category</label>
            <input
              className={`${errors.itemCategory && "input-error"}`}
              type="text"
              name="itemCategory"
              id="itemCategory"
              onChange={handleChange}
              value={tender && tender.itemCategory}
            />
            {errors.itemCategory && (
              <p className="error">{errors.itemCategory}</p>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="contractPeriod">Contract Period (In Months)</label>
            <input
              className={`${errors.contractPeriod && "input-error"}`}
              type="number"
              name="contractPeriod"
              id="contractPeriod"
              onChange={handleChange}
              value={tender && tender.contractPeriod}
            />
            {errors.contractPeriod && (
              <p className="error">{errors.contractPeriod}</p>
            )}
          </Grid2>
        </Grid2>
      </div>
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={tender && tender.draftTenderId ? handleUpdate : handleNext}
          disabled={btnLoading}
        >
          {btnLoading ? "Submitting..." : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
